import React, { useRef } from "react";
import { toast } from "react-toastify";
import { withTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import { connectionTypeEnum } from "common/enums";

import { DocumentName, UploadImage } from "pages/Admin/TechnicalSanction/style";

import getFileExtension from "utils/getFileExtension";

import xlsImgs from "assets/images/xlsx.png";

const validFileFormats = ["xls", "xlsx"];

const ConnectionTypeReadingModel = ({ t, handleUploadFile, handleClose }) => {
  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const ref = useRef();

  const formik = useFormik({
    initialValues: {
      connectionType: "",
      documents: {},
    },
    validationSchema: Yup.object({
      connectionType: Yup.string().required("Connection type is required"),
    }),
    onSubmit: async ({ documents, connectionType }) => {
      if (documents.file) {
        try {
          await handleUploadFile(documents.file, connectionType);
          handleClose();
        } catch (err) {}
      } else {
        toast.error("Please upload xls or xlsx file.");
      }
    },
  });
  const {
    setFieldValue,
    handleSubmit,
    values: { documents },
  } = formik;

  const fileChangeHandler = (e) => {
    const isValidFile = validFileFormats.includes(
      getFileExtension(e.target.files[0].name.toLowerCase())
    );
    if (!isValidFile) {
      toast.error(
        "Please upload xls or xlsx file and size should be less than 10 mb"
      );
    } else {
      setFieldValue("documents", {
        url: URL.createObjectURL(e.target.files[0]),
        name: e.target.files[0].name,
        file: e.target.files[0],
      });
    }
    ref.current.value = "";
  };

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title="Connection Type Reading Model"
      maxWidth="md"
    >
      <Grid container className="mt-3" xs={12}>
        <Grid item xs={12} sm={10}>
          <SelectField
            formik={formik}
            name="connectionType"
            label={t("Connection Type") + "*"}
            options={connectionTypeEnum}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography>{t("Upload Your File")}</Typography>
          <Grid>
            <div className="mb-3 d-flex gap-3 flex-wrap">
              {documents.file ? (
                <div className="mt-4 image-container">
                  <a href={documents.url} target="_blank" rel="noreferrer">
                    <Box
                      component="img"
                      className="image d-block m-auto"
                      alt="Xlsx File"
                      src={xlsImgs}
                    />
                  </a>
                  <i
                    className="ri-close-circle-line fs-3 close-btn"
                    onClick={() => {
                      setFieldValue("documents", {});
                    }}
                  />
                  <Tooltip title={documents.name}>
                    <DocumentName fontSize={13} variant="body2">
                      {documents.name}
                    </DocumentName>
                  </Tooltip>
                </div>
              ) : (
                <Box component="label" className="mt-2 m-0 p-0 cursor-pointer">
                  <UploadImage isTechnicalFeasibility>
                    <AddIcon
                      sx={{
                        color: "black",
                      }}
                    />
                  </UploadImage>
                  <input
                    ref={ref}
                    type="file"
                    accept="xlsx xls/*"
                    onChange={fileChangeHandler}
                    hidden
                  />
                </Box>
              )}
            </div>
          </Grid>
          <div>
            <Button
              sx={{ float: "right" }}
              color="primary"
              className={isMobileDevice ? "mt-2" : "mb-2"}
              variant="contained"
              type="submit"
              onClick={handleSubmit}
            >
              {t("Submit")}
            </Button>
          </div>
        </Grid>
      </Grid>
    </Modal>
  );
};

ConnectionTypeReadingModel.propTypes = {
  t: PropTypes.func,
  handleUploadFile: PropTypes.func,
  handleClose: PropTypes.func,
};

export default withTranslation()(ConnectionTypeReadingModel);
